import { isWidgetContext } from "@/lib/widget-utils";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Simple cache for loaded translations
const cache = new Map<string, Record<string, unknown>>();

// Configure detection based on context
const getDetectionConfig = () => {
  const isWidget = isWidgetContext();

  if (isWidget) {
    // In widget context, avoid persistent browser storage. Embedded widgets can
    // run in restrictive third-party iframe contexts where localStorage/cookies
    // are denied and throw SecurityError during app boot.
    // No htmlTag either: widget.edge hardcodes lang="en", which pinned every
    // visitor to English. Follow the visitor's browser instead; a per-widget
    // forced language (settings.language) is applied on top by useWidgetLanguage.
    return {
      order: ["navigator"],
      caches: [],
    };
  } else {
    // In main app context, use both localStorage and cookies
    return {
      order: ["localStorage", "cookie", "htmlTag", "navigator"],
      caches: ["localStorage", "cookie"],
    };
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use({
    type: "backend",
    read: async (
      language: string,
      namespace: string,
      callback: (error: Error | null, data: Record<string, unknown> | null) => void,
    ) => {
      const key = `${language}:${namespace}`;

      // Check cache first
      if (cache.has(key)) {
        callback(null, cache.get(key)!);
        return;
      }

      try {
        // Load translation dynamically
        const module = await import(`./locales/${language}/${namespace}.json`);
        const translation = module.default || module;

        // Cache the result
        cache.set(key, translation);
        callback(null, translation);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(`Failed to load translation: ${language}/${namespace}`, error);
        }
        callback(new Error(`Translation not found: ${language}/${namespace}`), null);
      }
    },
  })
  .init({
    supportedLngs: ["en", "es"],
    fallbackLng: "en",
    defaultNS: "common",
    detection: getDetectionConfig(),
    interpolation: {
      escapeValue: false,
    },
    pluralSeparator: "_",
    contextSeparator: "_",
  });

export default i18n;
